import React, { useMemo, useRef, useState } from 'react';
import Title from '../component-renderer/Title';
import Button from '../component-renderer/Button';
import Table from '../component-renderer/Table';
import Input from '../component-renderer/Input';
import Dropdown from '../component-renderer/Dropdown';
import FileUploadInput from '../component-renderer/FileUploadInput';
import SectionContainer from '../component-renderer/SectionContainer';

// Previously added
import Tag from '../component-renderer/Tag';
import Row from '../component-renderer/Row';
import Spacer from '../component-renderer/Spacer';
import Label from '../component-renderer/Label';
import Divider from '../component-renderer/Divider';

// NEW
import Grid from '../component-renderer/Grid';
import DatePicker from '../component-renderer/DatePicker';

const UiElementsTest = () => {
  const MOCK_USERS = [
    { id: 1, name: "Ada Lovelace", role: "Engineer", email: "ada@lab.io", createdAt: "2024-10-05", status: "active" },
    { id: 2, name: "Grace Hopper", role: "Scientist", email: "grace@navy.mil", createdAt: "2024-10-15", status: "inactive" },
    { id: 3, name: "Linus Torvalds", role: "Architect", email: "linus@kernel.org", createdAt: "2024-11-02", status: "active" },
    { id: 4, name: "Marie Curie", role: "Researcher", email: "marie@radium.fr", createdAt: "2024-12-01", status: "active" },
    { id: 5, name: "Alan Turing", role: "Cryptanalyst", email: "alan@bletchley.uk", createdAt: "2024-12-12", status: "inactive" },
  ];

  const columns = [
    { key: "name", header: "Name", sortable: true },
    { key: "role", header: "Role", sortable: true },
    { key: "email", header: "Email", sortable: false },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      accessor: (row) => new Date(row.createdAt),
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value) =>
        value === "active" ? (
          <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">
            Active
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
            Inactive
          </span>
        ),
    },
  ];

  // ---------- Form state ----------
  const [form, setForm] = useState({ name: "", role: "", email: "", startDate: "", endDate: "" });
  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // ---------- Table header filter ----------
  const [statusFilter, setStatusFilter] = useState('');
  const filteredUsers = useMemo(() => {
    if (!statusFilter) return MOCK_USERS;
    return MOCK_USERS.filter((u) => u.status === statusFilter);
  }, [statusFilter]);

  const roleOptions = [
    { label: "Engineer", value: "Engineer" },
    { label: "Scientist", value: "Scientist" },
    { label: "Architect", value: "Architect" },
    { label: "Researcher", value: "Researcher" },
    { label: "Cryptanalyst", value: "Cryptanalyst" },
  ];

  const statusOptions = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  // ---------- File upload demos (controlled) ----------
  const [filesDropzone, setFilesDropzone] = useState([]);
  const [filesButton, setFilesButton] = useState([]);
  const [filesCompact, setFilesCompact] = useState([]);
  const [filesUnstyled, setFilesUnstyled] = useState([]);
  const [dirFiles, setDirFiles] = useState([]);

  // ---------- Attach via icon on Input ----------
  const [filesAttach, setFilesAttach] = useState([]);
  const attachRef = useRef(null);

  // ---------- SectionContainer demo toggles ----------
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersEmpty, setUsersEmpty] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(true);

  // ---------- Tags demo state ----------
  const [skills, setSkills] = useState(['React', 'Flask', 'SQL']);
  const removeSkill = (idx) => setSkills((prev) => prev.filter((_, i) => i !== idx));

  // ---------- DatePicker demo state ----------
  const [hireDate, setHireDate] = useState(''); // single date
  const todayISO = new Date().toISOString().slice(0, 10);

  // disable weekends example
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-10">
      <Title
        as="h1"
        level={1}
        description="A demonstration of all reusable UI components working together."
        spacing="loose"
      >
        UI Components Demo
      </Title>

      {/* ---------------- Layout: Grid ---------------- */}
      <SectionContainer
        title="Layout: Grid"
        description="Responsive columns, spans, and auto-fit layouts."
        variant="bordered"
        size="md"
      >
        <Title as="h3" level={3} spacing="tight">Fixed columns per breakpoint</Title>
        <Grid cols={1} sm={2} md={3} lg={4} gap="md">
          {[...Array(8)].map((_, i) => (
            <Grid.Item key={i}>
              <div className="rounded border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-sm font-medium text-gray-800">Card {i + 1}</div>
                <p className="mt-1 text-sm text-gray-600">Auto-resizes with breakpoints.</p>
              </div>
            </Grid.Item>
          ))}
        </Grid>

        <Divider />

        <Title as="h3" level={3} spacing="tight">Item spans</Title>
        <Grid cols={4} gap="md" rowGap="sm">
          <Grid.Item colSpan={2}>
            <div className="rounded border border-gray-200 bg-white p-4">col-span-2</div>
          </Grid.Item>
          <Grid.Item>
            <div className="rounded border border-gray-200 bg-white p-4">col-span-1</div>
          </Grid.Item>
          <Grid.Item>
            <div className="rounded border border-gray-200 bg-white p-4">col-span-1</div>
          </Grid.Item>
          <Grid.Item colSpan={3}>
            <div className="rounded border border-gray-200 bg-white p-4">col-span-3</div>
          </Grid.Item>
          <Grid.Item>
            <div className="rounded border border-gray-200 bg-white p-4">col-span-1</div>
          </Grid.Item>
        </Grid>

        <Divider />

        <Title as="h3" level={3} spacing="tight">Auto-fit with min column width</Title>
        <Grid autoFit minColWidth="16rem" gap="lg">
          {[...Array(7)].map((_, i) => (
            <Grid.Item key={i}>
              <div className="rounded border border-gray-200 bg-white p-6 text-center">
                Auto-fit box {i + 1}
              </div>
            </Grid.Item>
          ))}
        </Grid>
      </SectionContainer>

      {/* ---------------- Form: DatePicker ---------------- */}
      <SectionContainer
        title="Form: DatePicker"
        description="Single-date selection with constraints and disabled days."
        variant="card"
        size="md"
        footer={<div className="text-sm text-gray-500">Tip: Use arrow keys inside the calendar; Enter selects a date.</div>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker
            label="Hire Date"
            placeholder="YYYY-MM-DD"
            value={hireDate}
            onChange={(_, next) => setHireDate(next)}
            helperText={hireDate ? `Selected: ${hireDate}` : 'Pick a date'}
            clearable
            minDate="2020-01-01"
            maxDate={todayISO}
            isDateDisabled={isWeekend}
            weekStartsOn="mon"
          />

          <DatePicker
            label="Simple (Uncontrolled)"
            defaultValue=""
            onValueChange={() => {}}
            variant="filled"
            size="sm"
            helperText="This one is uncontrolled and filled."
            clearable
          />
        </div>
      </SectionContainer>

      {/* ---------------- Layout Primitives / Previous New Components ---------------- */}
      <SectionContainer
        title="Layout Primitives"
        description="Tag, Row, Spacer, Label, Divider"
        variant="bordered"
        size="md"
      >
        <Title as="h3" level={3} spacing="tight">Tags Row</Title>
        <Row gap="sm" divider align="center" justify="start">
          {skills.map((s, i) => (
            <Tag key={s} color="primary" variant="soft" removable onRemove={() => removeSkill(i)}>
              {s}
            </Tag>
          ))}
          <Button size="sm" variant="outline" onClick={() => setSkills((p) => [...p, `Skill ${p.length + 1}`])}>
            + Add
          </Button>
        </Row>

        <Spacer size="md" />

        <div className="flex flex-col items-center">
          <Label size="xs" color="muted">Start</Label>
          <Spacer size="sm" />
          <Label size="sm" color="primary" weight="semibold">↓ API Calls ↓</Label>
          <Spacer size="sm" />
          <Label size="xs" color="muted">End</Label>
        </div>

        <Spacer size="lg" />
        <Title as="h4" level={4} spacing="tight">Dividers</Title>
        <Divider />
        <Divider variant="dashed" />
        <Divider label={<Label size="xs" color="neutral">With Label</Label>} />
        <Divider variant="dotted" label={<Label size="xs" color="muted">Dotted w/ Label</Label>} />

        <Spacer size="lg" />
        <Row gap="lg" align="stretch" justify="start">
          <div className="flex-1 rounded border border-gray-200 p-4">
            <Title as="h5" level={5} spacing="tight">Left Pane</Title>
            <p className="text-sm text-gray-600">Content A</p>
          </div>
          <Divider orientation="vertical" />
          <div className="flex-1 rounded border border-gray-200 p-4">
            <Title as="h5" level={5} spacing="tight">Right Pane</Title>
            <p className="text-sm text-gray-600">Content B</p>
          </div>
        </Row>
      </SectionContainer>

      {/* ---------------- Add New User ---------------- */}
      <SectionContainer
        title="Add New User"
        description="Create a new employee record"
        variant="card"
        size="md"
        actions={<Button size="sm" variant="ghost">Help</Button>}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" color="neutral">Cancel</Button>
            <Button color="primary" variant="solid">Submit</Button>
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Full Name"
            placeholder="John Doe"
            value={form.name}
            onChange={handleChange("name")}
            required
          />
          <Dropdown
            label="Role"
            name="role"
            placeholder="Select a role"
            value={form.role}
            onChange={(_, val) => setForm((f) => ({ ...f, role: val }))}
            options={roleOptions}
            required
            variant="outline"
          />
          <Input
            label="Email"
            type="email"
            placeholder="user@example.com"
            value={form.email}
            onChange={handleChange("email")}
            required
          />
          <DatePicker
            label="Start Date"
            value={form.startDate}
            onChange={(_, next) => setForm((f) => ({ ...f, startDate: next }))}
            helperText={form.startDate ? `Starts on ${form.startDate}` : 'Optional'}
            clearable
          />
        </div>
      </SectionContainer>

      {/* ---------------- Users Table ---------------- */}
      <SectionContainer
        title="Users"
        description="Manage members of your workspace"
        variant="shadow"
        size="md"
        collapsible
        expanded={usersExpanded}
        onExpandedChange={setUsersExpanded}
        loading={usersLoading}
        empty={usersEmpty}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setUsersLoading((v) => !v)}>
              {usersLoading ? 'Stop Loading' : 'Simulate Loading'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setUsersEmpty((v) => !v)}>
              {usersEmpty ? 'Show Data' : 'Show Empty'}
            </Button>
            <Button size="sm" variant="solid" color="primary">
              Add User
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          data={filteredUsers}
          pageSize={5}
          striped
          hoverable
          selection="multi"
          header={
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center sm:justify-between w-full">
              <Title as="h3" level={3} spacing="tight">
                User List
              </Title>

              <div className="flex gap-3 items-center w-full sm:w-auto">
                <Dropdown
                  name="statusFilter"
                  placeholder="Filter by status"
                  value={statusFilter}
                  onChange={(_, val) => setStatusFilter(val)}
                  options={statusOptions}
                  clearable
                  variant="outline"
                  size="sm"
                  containerClassName="w-full sm:w-48"
                />
                <Button variant="solid" color="primary" size="sm">
                  Add User
                </Button>
              </div>
            </div>
          }
        />
      </SectionContainer>

      {/* ---------------- File Uploads ---------------- */}
      <SectionContainer
        title="File Uploads"
        description="Demonstration of file input variations"
        variant="bordered"
        size="lg"
        padding
        footer={<div className="text-sm text-gray-500">Tip: Drag images to the dropzone or click to browse.</div>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* ... (unchanged examples) ... */}
        </div>
      </SectionContainer>
    </div>
  );
};

export default UiElementsTest;